import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { TSTitleCard } from '@/components/shared/card/Card';
import { dateToApiFormat, secondsToTimeString } from '@/helpers/dateHelper';
import { useShiftDetailsForDayByUserId } from '@/services/employee/useShiftDetailsForDay';
import { strings } from '@/services/translation/strings';
import { useMyLocation } from '@/services/useLocations';
import { format } from 'date-fns';
import { FC, useMemo } from 'react';
import { AiOutlineCalendar } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';

interface TeamMemberCalendarDayViewRosterCardProps {
  dateObj: Date;
  userId: string;
}

export const TeamMemberCalendarDayViewRosterCard: FC<TeamMemberCalendarDayViewRosterCardProps> = ({
  dateObj,
  userId
}) => {
  const navigate = useNavigate();
  const shiftDetails = useShiftDetailsForDayByUserId(dateObj, userId);

  const location = useMyLocation();

  const timelineItems = useMemo(() => {
    if (!shiftDetails) return [];

    const breaks = (shiftDetails.breaks ?? []).map(b => ({
      start: b.start,
      end: b.end,
      label: strings.timeshift.teamMember.calendarDayView.breakLabel
    }));

    return breaks.sort((a, b) => a.start - b.start);
  }, [shiftDetails]);

  const onViewRoster = () => {
    navigate(`/rosters/${dateToApiFormat(dateObj)}`);
  };

  return (
    <TSTitleCard
      title={format(dateObj, 'EEEE do MMMM yyyy')}
      descriptor={strings.timeshift.teamMember.calendarDayView.roster}
      icon={AiOutlineCalendar}
      secondaryAction={{
        label: strings.timeshift.teamMember.calendarDayView.viewDetails,
        onClick: onViewRoster
      }}
    >
      <SubmissionErrorBoundary>
        {shiftDetails ? (
          <div className="flex flex-col space-y-5">
            <div className="flex flex-row space-x-14">
              <div>
                <p className="font-semibold">{strings.timeshift.teamMember.calendarDayView.shiftStart}</p>
                <p>{secondsToTimeString(shiftDetails.start)}</p>
              </div>
              {timelineItems.map((item, index) => (
                <div key={index}>
                  <p className="font-semibold">{item.label}</p>
                  <p>{`${secondsToTimeString(item.start)} - ${secondsToTimeString(item.end)}`}</p>
                </div>
              ))}
              <div>
                <p className="font-semibold">{strings.timeshift.teamMember.calendarDayView.shiftEnd}</p>
                <p>{secondsToTimeString(shiftDetails.end)}</p>
              </div>
            </div>
            {location && (
              <div>
                <p className="font-semibold">{strings.timeshift.teamMember.calendarDayView.shiftLocation}</p>
                <p>{location.name}</p>
              </div>
            )}
          </div>
        ) : (
          <p className="font-semibold">{strings.timeshift.teamMember.calendarDayView.noShiftDetails}</p>
        )}
      </SubmissionErrorBoundary>
    </TSTitleCard>
  );
};
